// src/components/BrokerProfile.js
import { formatDisplayName } from "../utils/displayName";
import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import BrokerDashboardLayout from "../components/BrokerDashboardLayout";
import BrokerNotifications from "../components/BrokerNotifications";
import ChangePasswordPanel from "../components/ChangePasswordPanel";
import PortalFeedback from "../components/PortalFeedback";
import { MailPlus, Pencil, RefreshCw, ShieldCheck, Trash2, UsersRound } from "lucide-react";
import { isTimeoutError, withTimeout } from "../utils/asyncTimeout";

const PROFILE_TIMEOUT_MS = 25000;

/** Same panel style as Projects page */
const PANEL =
  "rounded-2xl border border-white/15 bg-white/70 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.08)]";

export default function BrokerProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileEditing, setProfileEditing] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [account, setAccount] = useState(null);
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [teamMessage, setTeamMessage] = useState("");
  const [teamError, setTeamError] = useState("");
  const [inviteForm, setInviteForm] = useState({
    email: "",
    role: "assistant",
  });
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
  });

  const fetchTeam = async () => {
    setTeamLoading(true);
    setTeamError("");

    try {
      const { data: authData, error: authError } = await withTimeout(
        supabase.auth.getUser(),
        PROFILE_TIMEOUT_MS,
        "Team session check timed out"
      );
      const user = authData?.user;

      if (authError || !user?.id) {
        setTeamError("You must be signed in to manage account users.");
        return;
      }

      const { data: memberRows, error: memberError } = await withTimeout(
        supabase
          .from("broker_account_members")
          .select("broker_account_id, role, broker_accounts(id, name, owner_user_id)")
          .eq("user_id", user.id)
          .order("created_at", { ascending: true })
          .limit(1),
        PROFILE_TIMEOUT_MS,
        "Broker account team lookup timed out"
      );

      if (memberError) {
        setTeamError(memberError.message || "Could not load broker account.");
        return;
      }

      const currentMembership = memberRows?.[0];
      const accountId = currentMembership?.broker_account_id;
      const currentAccount = currentMembership?.broker_accounts || null;

      setAccount(currentAccount);

      if (!accountId) {
        setMembers([]);
        setInvites([]);
        return;
      }

      const nowIso = new Date().toISOString();
      const [{ data: accountMembers, error: membersError }, { data: accountInvites, error: invitesError }] =
        await Promise.all([
          withTimeout(
            supabase.rpc("list_broker_account_team", {
              target_broker_account_id: accountId,
            }),
            PROFILE_TIMEOUT_MS,
            "Broker account members lookup timed out"
          ),
          withTimeout(
            supabase
              .from("broker_account_invites")
              .select("id, email, role, status, created_at, expires_at")
              .eq("broker_account_id", accountId)
              .eq("status", "pending")
              .gt("expires_at", nowIso)
              .order("created_at", { ascending: false }),
            PROFILE_TIMEOUT_MS,
            "Broker account invites lookup timed out"
          ),
        ]);

      if (membersError) {
        setTeamError(membersError.message || "Could not load account members.");
        return;
      }

      if (invitesError) {
        setTeamError(invitesError.message || "Could not load pending invites.");
        return;
      }

      setMembers(
        (accountMembers || []).map((member) => ({
          ...member,
          isCurrentUser: member.user_id === user.id,
        }))
      );
      setInvites(accountInvites || []);
    } catch (error) {
      setTeamError(
        isTimeoutError(error)
          ? "Team settings took too long to load. Refresh the page or sign out and back in."
          : "Could not load team settings."
      );
    } finally {
      setTeamLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;

    const fetchProfile = async () => {
      setErrorText("");
      try {
        const {
          data: { user },
          error: authError,
        } = await withTimeout(
          supabase.auth.getUser(),
          PROFILE_TIMEOUT_MS,
          "Profile session check timed out"
        );

        if (!alive) return;

        if (authError || !user) {
          setErrorText("You must be signed in to manage your profile.");
          setLoading(false);
          return;
        }

        const { data, error } = await withTimeout(
          supabase
            .from("broker_profiles")
            .select("*")
            .eq("user_id", user.id)
            .maybeSingle(),
          PROFILE_TIMEOUT_MS,
          "Broker profile lookup timed out"
        );

        if (!alive) return;

        if (error && error.code === "PGRST116") {
          setProfile({ user_id: user.id, email: user.email || "" });
          setLoading(false);
          return;
        }

        if (error) {
          setErrorText(error.message || "Could not load your profile.");
          setProfile({ user_id: user.id, email: user.email || "" });
          setLoading(false);
          return;
        }

        if (data) {
          setProfile(data);
          setFormData({
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            phone: data.phone || "",
          });
        } else {
          setProfile({ user_id: user.id, email: user.email || "" });
        }

        setLoading(false);
        fetchTeam();
      } catch (error) {
        if (!alive) return;
        setErrorText(
          isTimeoutError(error)
            ? "Profile took too long to load. Refresh the page or sign out and back in."
            : "Could not load your profile."
        );
        setLoading(false);
      }
    };

    fetchProfile();

    return () => {
      alive = false;
    };
  }, []);

  const handleSave = async () => {
    if (!profile?.user_id) return;

    setSaving(true);
    setErrorText("");
    setSuccessText("");

    const firstName = formData.first_name.trim();
    const lastName = formData.last_name.trim();
    const phone = formData.phone.trim();
    const fullName = `${firstName} ${lastName}`.trim();

    const { data: authUpdate, error: authUpdateError } = await withTimeout(
      supabase.auth.updateUser({
        data: {
          first_name: firstName || null,
          last_name: lastName || null,
        },
      }),
      PROFILE_TIMEOUT_MS,
      "Profile update timed out"
    ).catch((error) => ({ error }));

    if (authUpdateError) {
      setSaving(false);
      setErrorText(
        isTimeoutError(authUpdateError)
          ? "Profile took too long to save. Refresh and try again."
          : authUpdateError.message || "Could not update your login profile."
      );
      return;
    }

    const payload = {
      user_id: profile.user_id,
      email: authUpdate?.user?.email || profile.email || null,
      first_name: firstName || null,
      last_name: lastName || null,
      phone: phone || null,
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await withTimeout(
      supabase
        .from("broker_profiles")
        .upsert(payload, { onConflict: "user_id" })
        .select("*")
        .single(),
      PROFILE_TIMEOUT_MS,
      "Profile save timed out"
    ).catch((error) => ({ error }));

    setSaving(false);

    if (error) {
      setErrorText(
        isTimeoutError(error)
          ? "Profile took too long to save. Refresh and try again."
          : error.message || "Could not save your profile."
      );
      return;
    }

    if (account?.id && account?.owner_user_id === profile.user_id && fullName) {
      const { error: accountError } = await withTimeout(
        supabase
          .from("broker_accounts")
          .update({ name: fullName })
          .eq("id", account.id)
          .eq("owner_user_id", profile.user_id),
        PROFILE_TIMEOUT_MS,
        "Broker account save timed out"
      ).catch((error) => ({ error }));

      if (accountError) {
        setErrorText(
          isTimeoutError(accountError)
            ? "Broker account name took too long to save. Refresh and try again."
            : accountError.message || "Could not update your broker account name."
        );
        return;
      }

      setAccount((current) => (current ? { ...current, name: fullName } : current));
    }

    setProfile(data);
    setFormData({
      first_name: data.first_name || "",
      last_name: data.last_name || "",
      phone: data.phone || "",
    });
    setSuccessText("Saved.");
    setProfileEditing(false);
    await fetchTeam();
  };

  const resetProfileForm = () => {
    setFormData({
      first_name: profile?.first_name || "",
      last_name: profile?.last_name || "",
      phone: profile?.phone || "",
    });
    setErrorText("");
    setSuccessText("");
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    setTeamSaving(true);
    setTeamError("");
    setTeamMessage("");

    const cleanEmail = inviteForm.email.trim().toLowerCase();
    if (!cleanEmail) {
      setTeamError("Enter an email address.");
      setTeamSaving(false);
      return;
    }

    const { data: invite, error } = await withTimeout(
      supabase.rpc("invite_broker_member", {
        target_email: cleanEmail,
        target_role: inviteForm.role,
        target_broker_account_id: account?.id || null,
      }),
      PROFILE_TIMEOUT_MS,
      "Broker invite creation timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setTeamError(
        isTimeoutError(error)
          ? "Invite took too long to create. Refresh and check pending invites before trying again."
          : error.message || "Could not create invite."
      );
      setTeamSaving(false);
      return;
    }

    const { error: sendError } = await withTimeout(
      supabase.functions.invoke("send-team-invite", {
        body: {
          type: "broker",
          invite_id: invite?.id,
        },
      }),
      PROFILE_TIMEOUT_MS,
      "Broker invite email timed out"
    ).catch((error) => ({ error }));

    if (sendError) {
      setTeamError(
        isTimeoutError(sendError)
          ? "Invite was created, but the email send took too long. Refresh and check pending invites."
          : sendError.message || "Invite was created, but the email could not be sent."
      );
      await fetchTeam();
      setTeamSaving(false);
      return;
    }

    setInviteForm({ email: "", role: "assistant" });
    setTeamMessage(`Invite sent to ${cleanEmail}.`);
    await fetchTeam();
    setTeamSaving(false);
  };

  const handleRoleChange = async (member, role) => {
    setTeamSaving(true);
    setTeamError("");
    setTeamMessage("");

    const { error } = await withTimeout(
      supabase.rpc("set_broker_member_role", {
        target_user_id: member.user_id,
        target_role: role,
        target_broker_account_id: account?.id || null,
      }),
      PROFILE_TIMEOUT_MS,
      "Broker member role update timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setTeamError(
        isTimeoutError(error)
          ? "Role update took too long. Refresh and check the team list before trying again."
          : error.message || "Could not update member role."
      );
      setTeamSaving(false);
      return;
    }

    setTeamMessage("Member role updated.");
    await fetchTeam();
    setTeamSaving(false);
  };

  const handleRemoveMember = async (member) => {
    const label = member.email || member.user_id;
    const confirmed = window.confirm(`Remove ${label} from this broker account?`);
    if (!confirmed) return;

    setTeamSaving(true);
    setTeamError("");
    setTeamMessage("");

    const { error } = await withTimeout(
      supabase.rpc("remove_broker_member", {
        target_user_id: member.user_id,
        target_broker_account_id: account?.id || null,
      }),
      PROFILE_TIMEOUT_MS,
      "Broker member removal timed out"
    ).catch((error) => ({ error }));

    if (error) {
      setTeamError(
        isTimeoutError(error)
          ? "Remove took too long. Refresh and check the team list before trying again."
          : error.message || "Could not remove member."
      );
      setTeamSaving(false);
      return;
    }

    setTeamMessage("Member removed.");
    await fetchTeam();
    setTeamSaving(false);
  };

  const displayMemberName = (member) => {
    const first = member.first_name || "";
    const last = member.last_name || "";
    const fullName = `${first} ${last}`.trim();
    return formatDisplayName(fullName) || member.email || "Account user";
  };

  const roleLabel = (role) => {
    if (role === "owner") return "Owner";
    if (role === "admin") return "Admin";
    if (role === "assistant") return "Team";
    return "Member";
  };

  if (loading) {
    return (
      <BrokerDashboardLayout>
        <div className="py-6">Loading…</div>
      </BrokerDashboardLayout>
    );
  }

  return (
    <BrokerDashboardLayout>
 <div className="space-y-8 text-[#1A1A1A]">

  {/* HEADER — matches other pages */}
  <div>
    <h1 className="text-2xl font-extrabold tracking-tight">
      Profile
    </h1>
    <p className="text-xs text-black/60 mt-1">
      Manage your account and notification preferences
    </p>
  </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)]">
          <div className="space-y-6">
            {/* --- Account Info Panel --- */}
            <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-lg sm:text-xl font-bold tracking-tight">Account Information</h2>
                  <p className="text-sm text-black/60">
                    Update the name and phone number shown across your portal account.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (profileEditing) resetProfileForm();
                    setProfileEditing((current) => !current);
                  }}
                  className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-4 text-sm font-semibold text-black hover:bg-black/[0.03]"
                >
                  <Pencil className="h-4 w-4" />
                  {profileEditing ? "Cancel" : "Modify"}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 xl:grid-cols-1">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name</label>
                  <input
                    type="text"
                    value={profileEditing ? formData.first_name : formatDisplayName(formData.first_name)}
                    readOnly={!profileEditing}
                    onChange={(e) =>
                      setFormData({ ...formData, first_name: e.target.value })
                    }
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name</label>
                  <input
                    type="text"
                    value={profileEditing ? formData.last_name : formatDisplayName(formData.last_name)}
                    readOnly={!profileEditing}
                    onChange={(e) =>
                      setFormData({ ...formData, last_name: e.target.value })
                    }
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone (optional)</label>
                  <input
                    type="text"
                    value={formData.phone}
                    readOnly={!profileEditing}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    className={`w-full rounded-md border border-black/10 px-3 py-2 text-sm ${profileEditing ? "bg-white" : "bg-white text-black/70"}`}
                  />
                </div>
              </div>

	              <div className="pt-2">
	                {errorText && (
	                  <PortalFeedback type="error" className="mb-3">{errorText}</PortalFeedback>
	                )}
	                {successText && (
	                  <PortalFeedback type="success" className="mb-3">{successText}</PortalFeedback>
	                )}
                {profileEditing && (
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full rounded-md bg-[#EB040D] text-white px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                )}
              </div>
            </div>

            <div className={`${PANEL} px-4 sm:px-6 py-6`}>
              <ChangePasswordPanel />
            </div>

          </div>

          <div className="space-y-6 lg:self-start">
          <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
            {/* --- Notification Panel --- */}
            <div>
              <h2 className="text-lg sm:text-xl font-bold tracking-tight">Notification Preferences</h2>
              <p className="text-sm text-black/60">
                Manage how you’d like to receive project updates. You can update or turn these off anytime.
              </p>
            </div>

            <BrokerNotifications
              className="bg-transparent"
              panelClass="bg-white border border-black/10 rounded-lg p-4"
            />
          </div>

          {/* --- Broker Account Team Panel --- */}
          <div className={`${PANEL} px-4 sm:px-6 py-6 space-y-6`}>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <UsersRound className="h-5 w-5 text-[#EB040D]" />
                <h2 className="text-lg sm:text-xl font-bold tracking-tight">Broker Account Team</h2>
              </div>
              <p className="mt-1 max-w-3xl text-sm text-black/60">
                Add a team member so they can help submit and manage requests under the same broker account. Everyone on this account can see the same projects.
              </p>
            </div>
            <button
              type="button"
              onClick={fetchTeam}
              disabled={teamLoading}
              className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-3 text-sm font-medium hover:bg-black/[0.03] disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${teamLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="rounded-xl border border-black/10 bg-white/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-black/45">Account</p>
            <div className="mt-1 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-lg font-semibold">{formatDisplayName(account?.name) || "Broker Account"}</p>
                <p className="text-sm text-black/55">
                  {members.length} active {members.length === 1 ? "user" : "users"}
                  {invites.length > 0
                    ? ` • ${invites.length} pending invites`
                    : ""}
                </p>
              </div>
              <div className="inline-flex w-fit items-center gap-2 rounded-full bg-black/[0.04] px-3 py-1 text-xs font-medium text-black/65">
                <ShieldCheck className="h-4 w-4 text-[#EB040D]" />
                Shared project access
              </div>
            </div>
          </div>

          <form onSubmit={handleInvite} className="grid grid-cols-1 gap-3 rounded-xl border border-black/10 bg-white/70 p-4 lg:grid-cols-[minmax(0,1fr)_180px_auto]">
            <div>
              <label className="block text-sm font-medium mb-1">Invite by Email</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="team@company.com"
                className="w-full rounded-md border border-black/10 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role</label>
              <select
                value={inviteForm.role}
                onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                className="w-full rounded-md border border-black/10 bg-white px-3 py-2 text-sm"
              >
                <option value="assistant">Team</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="submit"
                disabled={teamSaving || !account?.id}
                className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-md bg-[#EB040D] px-4 text-sm font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 lg:w-auto"
              >
                <MailPlus className="h-4 w-4" />
                Invite
              </button>
            </div>
          </form>

	          {(teamError || teamMessage) && (
	            <PortalFeedback type={teamError ? "error" : "success"}>
	              {teamError || teamMessage}
	            </PortalFeedback>
	          )}

          <div className="overflow-hidden rounded-xl border border-black/10 bg-white/70">
            <div className="grid grid-cols-12 border-b border-black/10 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-black/45">
              <div className="col-span-6">User</div>
              <div className="col-span-3">Role</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>

            {teamLoading ? (
              <div className="px-4 py-5 text-sm text-black/60">Loading team…</div>
            ) : members.length === 0 ? (
              <div className="px-4 py-5 text-sm text-black/60">No account members found.</div>
            ) : (
              members.map((member) => {
                const isOwner = member.role === "owner";
                return (
                  <div key={member.id} className="grid grid-cols-12 items-center gap-2 border-b border-black/5 px-4 py-3 last:border-b-0">
                    <div className="col-span-12 sm:col-span-6">
                      <p className="text-sm font-semibold">
                        {displayMemberName(member)}
                        {member.isCurrentUser ? <span className="ml-2 text-xs font-normal text-black/45">You</span> : null}
                      </p>
                      <p className="text-xs text-black/50">{member.email || member.user_id}</p>
                    </div>
                    <div className="col-span-6 sm:col-span-3">
                      {isOwner ? (
                        <span className="inline-flex rounded-full bg-black/[0.06] px-3 py-1 text-xs font-semibold text-black/70">
                          Owner
                        </span>
                      ) : member.role === "admin" ? (
                        <span className="inline-flex rounded-full bg-black/[0.06] px-3 py-1 text-xs font-semibold text-black/70">
                          Admin
                        </span>
                      ) : (
                        <select
                          value={member.role}
                          onChange={(e) => handleRoleChange(member, e.target.value)}
                          disabled={teamSaving}
                          className="w-full max-w-[160px] rounded-md border border-black/10 bg-white px-2 py-1.5 text-sm"
                          aria-label={`Change role for ${displayMemberName(member)}`}
                        >
                          <option value="assistant">Team</option>
                        </select>
                      )}
                    </div>
                    <div className="col-span-6 flex justify-end sm:col-span-3">
                      <button
                        type="button"
                        onClick={() => handleRemoveMember(member)}
                        disabled={teamSaving || isOwner || member.isCurrentUser}
                        className="inline-flex h-9 items-center justify-center gap-2 rounded-md border border-black/10 bg-white px-3 text-sm hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-40"
                        title={isOwner ? "Owners cannot be removed here" : "Remove member"}
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {invites.length > 0 && (
            <div className="rounded-xl border border-black/10 bg-white/70 p-4">
              <h3 className="text-sm font-semibold">Invites</h3>
              <div className="mt-3 space-y-2">
                {invites.map((invite) => (
                  <div key={invite.id} className="flex flex-col gap-1 rounded-md border border-black/5 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-black/50">{roleLabel(invite.role)} • {invite.status}</p>
                    </div>
                    <p className="text-xs text-black/45">
                      Expires {invite.expires_at ? new Date(invite.expires_at).toLocaleDateString() : "soon"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
          </div>
        </div>
      </div>
    </BrokerDashboardLayout>
  );
}
